import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { StyleProp, ViewStyle } from 'react-native';
import { View } from 'react-native';

import { MoreHorizontalIcon } from '@/assets';
import { useGetHappeningParticipants } from '@/features/api/hooks/grapevine-happening/useGetHappeningParticipants';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { AppRoute } from '@/navigation/types/navigation';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const MAX_AVATAR_NUMBER = 5;

type Props = {
  postId?: string;
  style?: StyleProp<ViewStyle>;
  participantCount?: number;
};

export const ParticipantsOverview: FC<Props> = ({
  postId,
  style,
  participantCount
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const { isFeatureEnabled } = useFeature();
  const isHappeningParticipantListEnabled = isFeatureEnabled(
    FeaturesEnum.HappeningParticipants
  );

  const {
    data: participants,
    isLoading,
    isError
  } = useGetHappeningParticipants(postId);

  if (isLoading || isError || !hasItems(participants?.content)) return <></>;

  return (
    <View style={[style, { gap: 24 }]}>
      {isHappeningParticipantListEnabled && (
        <View style={{ flexDirection: 'row' }}>
          {participants?.content
            ?.slice(0, MAX_AVATAR_NUMBER)
            .map((item, index) => {
              if (
                (participants?.content ?? []).length > MAX_AVATAR_NUMBER &&
                index > MAX_AVATAR_NUMBER - 2
              ) {
                return (
                  <View
                    key={item.id}
                    style={{
                      zIndex: MAX_AVATAR_NUMBER - index,
                      marginRight: -20
                    }}
                  >
                    <ProfileAvatar
                      key="placeholder"
                      viewSize={40}
                      borderWidth={4}
                      iconFallback={MoreHorizontalIcon}
                      fallbackColor={theme.colors.primary}
                      backgroundColor={theme.colors.secondaryContainer}
                    />
                  </View>
                );
              }
              return (
                <View
                  key={item.id}
                  style={{
                    zIndex: MAX_AVATAR_NUMBER - index,
                    marginRight: -20
                  }}
                >
                  <PersonAvatar borderWidth={4} person={item} size={40} />
                </View>
              );
            })}
        </View>
      )}
      <NavigationButton
        mode="text"
        screen={AppRoute.HappeningParticipants}
        params={{ postId, participantCount }}
        disabled={!isHappeningParticipantListEnabled}
        labelStyle={{
          textDecorationLine: isHappeningParticipantListEnabled
            ? 'underline'
            : 'none'
        }}
      >
        {t('interestedParticipants', {
          count: participants?.page?.totalElements ?? participantCount ?? 0
        })}
      </NavigationButton>
    </View>
  );
};
